import { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { LandingPage } from '../landing/LandingPage';
import { TriageScreen } from '../landing/TriageScreen';
import { ProcessingScreen } from '../landing/ProcessingScreen';
import { ChatScreen } from '../chat/ChatScreen';
import { ProtocolPage } from '../protocol/ProtocolPage';
import { CheckoutReview } from '../checkout/CheckoutReview';
import { getSessionId, setSessionId } from '../../utils/sessionId';
import { checkForExistingJourney, hydrateAppState, findActiveJourneyByUid } from '../../services/firestore/journeyResumeService';
import { ResumeModal } from './ResumeModal';
import { auth } from '../../services/firebase';

/**
 * Routes between screens based on AppContext state.
 * On mount, checks for an existing patient journey and offers to resume.
 * Checks both localStorage session ID AND authenticated user's UID.
 * The main flow: landing -> triage -> chat -> protocol
 * (processing screen kept as fallback for mock mode)
 */
export function ScreenRouter() {
  const { state, dispatch } = useApp();
  const resumeChecked = useRef(false);
  const [showResume, setShowResume] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(true);
  const resumeDataRef = useRef<ReturnType<typeof hydrateAppState> | null>(null);
  const resumeSessionIdRef = useRef<string | null>(null);

  // Check for existing journey on mount
  useEffect(() => {
    if (resumeChecked.current) return;
    resumeChecked.current = true;

    const sessionId = getSessionId();
    if (sessionId.startsWith('ephemeral_')) {
      setResumeLoading(false);
      return;
    }

    // Step 1: Check localStorage session ID
    checkForExistingJourney(sessionId)
      .then(async (result) => {
        if (result) {
          const hydrated = hydrateAppState(result);
          if (hydrated.triageData) {
            resumeDataRef.current = hydrated;
            setShowResume(true);
            setResumeLoading(false);
            return;
          }
        }

        // Step 2: If no localStorage match, check authenticated user's journeys
        const user = auth.currentUser;
        if (user && !user.isAnonymous && user.email) {
          const uidResult = await findActiveJourneyByUid(user.uid);
          if (uidResult) {
            const hydrated = hydrateAppState(uidResult);
            if (hydrated.triageData) {
              resumeDataRef.current = hydrated;
              resumeSessionIdRef.current = uidResult.journey.sessionId;
              setShowResume(true);
              setResumeLoading(false);
              return;
            }
          }
        }

        setResumeLoading(false);
      })
      .catch((err) => {
        console.warn('[Journey] Resume check failed:', err);
        setResumeLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleResume() {
    // If resuming from a UID-based journey, update localStorage session ID
    if (resumeSessionIdRef.current) {
      setSessionId(resumeSessionIdRef.current);
    }
    if (resumeDataRef.current) {
      dispatch({ type: 'HYDRATE', payload: resumeDataRef.current });
    }
    setShowResume(false);
  }

  function handleStartFresh() {
    setShowResume(false);
    // Stay on landing — user starts over
  }

  // Show loading state while checking for resume (brief)
  if (resumeLoading && state.screen === 'landing') {
    return null; // Invisible — avoids flash of landing page before resume modal
  }

  // Resume modal overlay
  if (showResume) {
    return (
      <>
        <LandingPage />
        <ResumeModal onResume={handleResume} onStartFresh={handleStartFresh} />
      </>
    );
  }

  switch (state.screen) {
    case 'triage':
      return <TriageScreen />;
    case 'processing':
      return <ProcessingScreen />;
    case 'chat':
      return <ChatScreen />;
    case 'protocol':
      return <ProtocolPage />;
    case 'checkout_review':
      return <CheckoutReview />;
    default:
      return <LandingPage />;
  }
}
